import React, { useMemo, useState } from 'react';
import { formatDecimal as fmt } from '../../helpers/paymentsHelper';

const FleetTable = ({ data }) => {
  const getByPath = (obj, path) =>
    path.split('.').reduce((acc, key) => (acc?.[key] ?? undefined), obj);

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumbers) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const [expandedRows, setExpandedRows] = useState([]);
  const [hasExpanded, setHasExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const handleRowClick = (id) => {
    setExpandedRows((prevExpandedRows) =>
      prevExpandedRows.includes(id)
        ? prevExpandedRows.filter((rowId) => rowId !== id)
        : [...prevExpandedRows, id]
    );
    setHasExpanded(!hasExpanded);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => dir * cmp(getByPath(a, sortConfig.key), getByPath(b, sortConfig.key)));
  }, [data, sortConfig]);

  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) =>
      value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="w-full h-full">
      <div className="px-1" style={{ maxWidth: '220px' }}>
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingTop: '0.44rem', paddingBottom: '0.44rem' }}
            className="w-full pl-4 pr-10 text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 placeholder-gray-400 placeholder-opacity-100 font-light text-sm"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </div>
      </div>
      <div
        className="overflow-y-auto w-full scrollbar"
        style={{ maxHeight: '600px' }}>
        <table className="border-collapse w-full h-10/12">
          <thead>
            <tr>
            <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort('item.fleet_name')}>
                Fleet{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1">
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === 'fleet_name' &&
                      sortConfig.direction === 'asc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === 'fleet_name' &&
                      sortConfig.direction === 'desc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort('current.payout')}>
                Payout{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1">
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === 'current.payout' &&
                      sortConfig.direction === 'asc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === 'current.payout' &&
                      sortConfig.direction === 'desc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort('current.access_to_site')}>
                Access to Site{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1">
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === 'current.access_to_site' && sortConfig.direction === 'asc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === 'current.access_to_site' &&
                      sortConfig.direction === 'desc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort('current.deduction')}>
                Deduction{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1">
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === 'current.deduction' && sortConfig.direction === 'asc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === 'current.deduction' &&
                      sortConfig.direction === 'desc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort('current.running_balance')}>
                Running Balance{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1">
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === 'current.running_balance' && sortConfig.direction === 'asc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === 'current.running_balance' &&
                      sortConfig.direction === 'desc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />
                </svg>
              </th>
              <th
                className="border-b p-2 cursor-pointer text-left"
                onClick={() => handleSort('current.total_trips')}>
                Total Trips{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="16"
                  height="32"
                  className="inline-block ml-1">
                  <path
                    d="M12 5l-5 5h10l-5-5z"
                    fill={
                      sortConfig.key === 'current.total_trips' && sortConfig.direction === 'asc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />

                  <path
                    d="M12 19l5-5H7l5 5z"
                    fill={
                      sortConfig.key === 'current.total_trips' &&
                      sortConfig.direction === 'desc'
                        ? '#745D96'
                        : '#9C9C9C'
                    }
                  />
                </svg>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => (
              <React.Fragment key={item.fleet_id}>
                <tr className="border-t border-b p-2 cursor-pointer">
                  <td className="border-t text-gray-400 border-b p-2 text-left">
                    {item.fleet_name}
                  </td>
                  <td className="border-t text-gray-400 border-b p-2 text-left">
                    {fmt(item.current.payout)}
                  </td>
                  <td className="border-t text-gray-400 border-b p-2 text-left">
                    {fmt(item.current.access_to_site)}
                  </td>
                  <td className="border-t text-gray-400 border-b p-2 text-left">
                    {fmt(item.current.deduction)}
                  </td>
                  <td className="border-t text-gray-400 border-b p-2 text-left">
                    {fmt(item.current.running_balance)}
                  </td>
                  <td className="border-t text-gray-400 border-b p-2 text-left">
                    {item.current.total_trips}
                  </td>
                  <td className="border-t border-b p-2 space-x-1">
                    <button
                      onClick={() => handleRowClick(item.fleet_id)}
                      className="p-2 bg-gray-700 rounded hover:bg-gray-200">
                      {hasExpanded ? (
                        <svg
                          width="20"
                          height="10"
                          viewBox="0 0 20 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M19 1C16.6 3.667 13.6 5 10 5C6.4 5 3.4 3.667 1 1M1 7.00001L3.5 3.20001M18.9998 6.97601L16.5078 3.20001M7 9L7.5 5M13 9L12.5 5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="14"
                          viewBox="0 0 20 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M8 7C8 7.53043 8.21071 8.03914 8.58579 8.41421C8.96086 8.78929 9.46957 9 10 9C10.5304 9 11.0391 8.78929 11.4142 8.41421C11.7893 8.03914 12 7.53043 12 7C12 6.46957 11.7893 5.96086 11.4142 5.58579C11.0391 5.21071 10.5304 5 10 5C9.46957 5 8.96086 5.21071 8.58579 5.58579C8.21071 5.96086 8 6.46957 8 7Z"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M19 7C16.6 11 13.6 13 10 13C6.4 13 3.4 11 1 7C3.4 3 6.4 1 10 1C13.6 1 16.6 3 19 7Z"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  </td>
                </tr>
                {expandedRows.includes(item.fleet_id) && (
                  <tr>
                    <td colSpan="12" className="p-0 pr-2">
                      <table className="table-fixed w-full border-collapse">
                        <thead className="w-full">
                          <tr>
                            <th className="border-b p-2 cursor-pointer text-right"></th>
                            <th className="text-gray-400 border-b p-2 font-semibold cursor-pointer text-right">
                              Week {item.current.week_start}
                            </th>
                            <th className="text-gray-400 border-b p-2 font-semibold cursor-pointer text-right">
                              Week {item.previous.week_start}
                            </th>
                            <th className="text-gray-400 border-b p-2 font-semibold cursor-pointer text-right">
                              Variance
                            </th>
                          </tr>
                        </thead>

                        <tbody className="w-full pr-2">
                          <tr>
                            <td className="text-gray-400 font-semibold border-t border-b p-2 text-left">
                              {item.fleet_name}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              Payout
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.current.payout)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.previous.payout)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {item.payout_variance}%
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 19 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={item.payout_variance === 0 ? 'hidden' : ''}
                                >
                                  {item.payout_variance > 0 ? 
                                  <path
                                    d="M1.2729 10.9217L0.306803 9.98025C-0.102268 9.58163 -0.102268 8.93706 0.306803 8.54268L8.76237 0.298962C9.17144 -0.0996541 9.83291 -0.0996541 10.2376 0.298962L18.6932 8.53844C19.1023 8.93706 19.1023 9.58163 18.6932 9.97601L17.7271 10.9174C17.3137 11.3203 16.6391 11.3118 16.2344 10.9005L11.2429 5.79478V17.9823C11.2429 18.5463 10.7773 19 10.1985 19H8.80589C8.2271 19 7.76145 18.5463 7.76145 17.9823V5.79478L2.76557 10.9047C2.36086 11.3203 1.68633 11.3288 1.2729 10.9217Z"
                                    fill="#00FF00"
                                  /> :
                                  <path d="M1.2729 8.07834L0.306803 9.01975C-0.102268 9.41837 -0.102268 10.0629 0.306803 10.4573L8.76237 18.701C9.17144 19.0997 9.83291 19.0997 10.2376 18.701L18.6932 10.4616C19.1023 10.0629 19.1023 9.41837 18.6932 9.02399L17.7271 8.08258C17.3137 7.67972 16.6391 7.6882 16.2344 8.09954L11.2429 13.2052V1.01774C11.2429 0.453745 10.7773 0 10.1985 0H8.80589C8.2271 0 7.76145 0.453745 7.76145 1.01774V13.2052L2.76557 8.0953C2.36086 7.67972 1.68633 7.67124 1.2729 8.07834Z" 
                                    fill="#FF0000"/>
                                  }
                                </svg>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              Access to Site
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.current.access_to_site)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.previous.access_to_site)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {item.access_variance}%
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 19 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={item.access_variance === 0 ? 'hidden' : ''}
                                >
                                  {item.access_variance > 0 ? 
                                  <path
                                    d="M1.2729 10.9217L0.306803 9.98025C-0.102268 9.58163 -0.102268 8.93706 0.306803 8.54268L8.76237 0.298962C9.17144 -0.0996541 9.83291 -0.0996541 10.2376 0.298962L18.6932 8.53844C19.1023 8.93706 19.1023 9.58163 18.6932 9.97601L17.7271 10.9174C17.3137 11.3203 16.6391 11.3118 16.2344 10.9005L11.2429 5.79478V17.9823C11.2429 18.5463 10.7773 19 10.1985 19H8.80589C8.2271 19 7.76145 18.5463 7.76145 17.9823V5.79478L2.76557 10.9047C2.36086 11.3203 1.68633 11.3288 1.2729 10.9217Z"
                                    fill="#00FF00"
                                  /> :
                                  <path d="M1.2729 8.07834L0.306803 9.01975C-0.102268 9.41837 -0.102268 10.0629 0.306803 10.4573L8.76237 18.701C9.17144 19.0997 9.83291 19.0997 10.2376 18.701L18.6932 10.4616C19.1023 10.0629 19.1023 9.41837 18.6932 9.02399L17.7271 8.08258C17.3137 7.67972 16.6391 7.6882 16.2344 8.09954L11.2429 13.2052V1.01774C11.2429 0.453745 10.7773 0 10.1985 0H8.80589C8.2271 0 7.76145 0.453745 7.76145 1.01774V13.2052L2.76557 8.0953C2.36086 7.67972 1.68633 7.67124 1.2729 8.07834Z" 
                                    fill="#FF0000"/>
                                  }
                                </svg>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              Deduction
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.current.deduction)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.previous.deduction)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {item.deduction_variance}%
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 19 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={item.deduction_variance === 0 ? 'hidden' :''}
                                >
                                  {item.deduction_variance > 0 ? 
                                  <path
                                    d="M1.2729 10.9217L0.306803 9.98025C-0.102268 9.58163 -0.102268 8.93706 0.306803 8.54268L8.76237 0.298962C9.17144 -0.0996541 9.83291 -0.0996541 10.2376 0.298962L18.6932 8.53844C19.1023 8.93706 19.1023 9.58163 18.6932 9.97601L17.7271 10.9174C17.3137 11.3203 16.6391 11.3118 16.2344 10.9005L11.2429 5.79478V17.9823C11.2429 18.5463 10.7773 19 10.1985 19H8.80589C8.2271 19 7.76145 18.5463 7.76145 17.9823V5.79478L2.76557 10.9047C2.36086 11.3203 1.68633 11.3288 1.2729 10.9217Z"
                                    fill="#00FF00"
                                  /> :
                                  <path d="M1.2729 8.07834L0.306803 9.01975C-0.102268 9.41837 -0.102268 10.0629 0.306803 10.4573L8.76237 18.701C9.17144 19.0997 9.83291 19.0997 10.2376 18.701L18.6932 10.4616C19.1023 10.0629 19.1023 9.41837 18.6932 9.02399L17.7271 8.08258C17.3137 7.67972 16.6391 7.6882 16.2344 8.09954L11.2429 13.2052V1.01774C11.2429 0.453745 10.7773 0 10.1985 0H8.80589C8.2271 0 7.76145 0.453745 7.76145 1.01774V13.2052L2.76557 8.0953C2.36086 7.67972 1.68633 7.67124 1.2729 8.07834Z" 
                                    fill="#FF0000"/>
                                  }
                                </svg>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              Running Balance
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.current.running_balance)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {fmt(item.previous.running_balance)}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {item.balance_variance}%
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 19 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={item.balance_variance === 0 ? 'hidden' :''}
                                >
                                  {item.balance_variance > 0 ? 
                                  <path
                                    d="M1.2729 10.9217L0.306803 9.98025C-0.102268 9.58163 -0.102268 8.93706 0.306803 8.54268L8.76237 0.298962C9.17144 -0.0996541 9.83291 -0.0996541 10.2376 0.298962L18.6932 8.53844C19.1023 8.93706 19.1023 9.58163 18.6932 9.97601L17.7271 10.9174C17.3137 11.3203 16.6391 11.3118 16.2344 10.9005L11.2429 5.79478V17.9823C11.2429 18.5463 10.7773 19 10.1985 19H8.80589C8.2271 19 7.76145 18.5463 7.76145 17.9823V5.79478L2.76557 10.9047C2.36086 11.3203 1.68633 11.3288 1.2729 10.9217Z"
                                    fill="#00FF00"
                                  /> :
                                  <path d="M1.2729 8.07834L0.306803 9.01975C-0.102268 9.41837 -0.102268 10.0629 0.306803 10.4573L8.76237 18.701C9.17144 19.0997 9.83291 19.0997 10.2376 18.701L18.6932 10.4616C19.1023 10.0629 19.1023 9.41837 18.6932 9.02399L17.7271 8.08258C17.3137 7.67972 16.6391 7.6882 16.2344 8.09954L11.2429 13.2052V1.01774C11.2429 0.453745 10.7773 0 10.1985 0H8.80589C8.2271 0 7.76145 0.453745 7.76145 1.01774V13.2052L2.76557 8.0953C2.36086 7.67972 1.68633 7.67124 1.2729 8.07834Z" 
                                    fill="#FF0000"/>
                                  }
                                </svg>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              Total Trips
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {item.current.total_trips}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              {item.previous.total_trips}
                            </td>
                            <td className="text-gray-400 border-t border-b p-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {item.trip_variance}%
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 19 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={item.trip_variance === 0 ? 'hidden' : ''}
                                >
                                  {item.trip_variance > 0 ? 
                                  <path
                                    d="M1.2729 10.9217L0.306803 9.98025C-0.102268 9.58163 -0.102268 8.93706 0.306803 8.54268L8.76237 0.298962C9.17144 -0.0996541 9.83291 -0.0996541 10.2376 0.298962L18.6932 8.53844C19.1023 8.93706 19.1023 9.58163 18.6932 9.97601L17.7271 10.9174C17.3137 11.3203 16.6391 11.3118 16.2344 10.9005L11.2429 5.79478V17.9823C11.2429 18.5463 10.7773 19 10.1985 19H8.80589C8.2271 19 7.76145 18.5463 7.76145 17.9823V5.79478L2.76557 10.9047C2.36086 11.3203 1.68633 11.3288 1.2729 10.9217Z"
                                    fill="#00FF00"
                                  /> :
                                  <path d="M1.2729 8.07834L0.306803 9.01975C-0.102268 9.41837 -0.102268 10.0629 0.306803 10.4573L8.76237 18.701C9.17144 19.0997 9.83291 19.0997 10.2376 18.701L18.6932 10.4616C19.1023 10.0629 19.1023 9.41837 18.6932 9.02399L17.7271 8.08258C17.3137 7.67972 16.6391 7.6882 16.2344 8.09954L11.2429 13.2052V1.01774C11.2429 0.453745 10.7773 0 10.1985 0H8.80589C8.2271 0 7.76145 0.453745 7.76145 1.01774V13.2052L2.76557 8.0953C2.36086 7.67972 1.68633 7.67124 1.2729 8.07834Z" 
                                    fill="#FF0000"/>
                                  }
                                </svg>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FleetTable;
